import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { DateInput } from "@/components/ui/date-input";
import { fetchAirportServices, getAirportService } from "@/lib/airportServicesApi";
import { computeItemBaseAmount } from "@/lib/financialCalculations";
import { PriceWithAdjustment } from "@/components/proposals/PriceWithAdjustment";

interface AirportServicesStepProps {
  data: any;
  onChange: (data: any) => void;
  readOnly?: boolean;
}

const pricingTypeLabel = (type?: string): string | null => {
  switch ((type || "").toLowerCase()) {
    case "per_person":
      return "Per person";
    case "per_family":
      return "Per family";
    case "per_group":
      return "Per group";
    default:
      return null;
  }
};

const AirportServicesStep = ({ data, onChange, readOnly }: AirportServicesStepProps) => {
  const isReadOnly = !!readOnly;
  // During editing the persisted pax_* fields are stale (synced only on save),
  // so use the live passengerCount to price per-person airport services correctly.
  const paxData = {
    ...data,
    pax_adults: data?.passengerCount?.adults ?? data?.pax_adults ?? 0,
    pax_children: data?.passengerCount?.children ?? data?.pax_children ?? 0,
    pax_infants: data?.passengerCount?.infants ?? data?.pax_infants ?? 0,
  };
  const paxCount = Math.max(
    1,
    (paxData.pax_adults || 0) + (paxData.pax_children || 0) + (paxData.pax_infants || 0),
  );
  const [services, setServices] = useState<any[]>([]);
  const [airportServices, setAirportServices] = useState<any[]>([]);
  const didInit = useRef(false);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (didInit.current) return;
    if (!Array.isArray(data.airportServices)) return; // wait for load
    const loadedServices = data.airportServices as any[];
    const sorted = [...loadedServices].sort((a, b) => {
      if (!a.service_date) return -1;
      if (!b.service_date) return 1;
      return a.service_date.localeCompare(b.service_date);
    });
    setAirportServices(sorted);
    didInit.current = true;
  }, []); // Only run once on mount

  const fetchServices = async () => {
    const response = await fetchAirportServices({ page: 1, size: 2000, sortField: "service_name", sortOrder: "asc" });
    setServices(response.items || []);
  };

  const addService = () => {
    if (isReadOnly) return;
    setAirportServices([{ service_date: "", free_of_charge: false, notes: "" }, ...airportServices]);
  };

  const removeService = (index: number) => {
    if (isReadOnly) return;
    const newServices = airportServices.filter((_, i) => i !== index);
    setAirportServices(newServices);
    onChange({ ...data, airportServices: newServices });
  };

  const updateService = async (index: number, field: string, value: any) => {
    if (isReadOnly) return;
    const newServices = [...airportServices];
    newServices[index] = { ...newServices[index], [field]: value };
    if (field === "airport_service_id") {
      newServices[index].airport_services = undefined;
      setAirportServices(newServices);
      onChange({ ...data, airportServices: newServices });

      if (value) {
        try {
          const selected = await getAirportService(Number(value));
          const updated = [...newServices];
          updated[index] = {
            ...updated[index],
            airport_services: {
              id: selected.id,
              service_code: selected.service_code,
              service_name: selected.service_name,
              description: selected.description,
              airport: selected.airport,
              pricing_type: selected.pricing_type,
              price_op: selected.price_op,
              price_ag: selected.price_ag,
              price_cf: selected.price_cf,
            },
          };
          setAirportServices(updated);
          onChange({ ...data, airportServices: updated });
        } catch {
          // Ignore lookup errors and keep manual selection flow.
        }
      }
      return;
    }
    setAirportServices(newServices);
    onChange({ ...data, airportServices: newServices });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Airport Services</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Add airport services for arrival, departure, or transit
          </p>
        </div>
        <Button type="button" size="sm" onClick={addService} disabled={isReadOnly}>
          <Plus className="mr-2 h-4 w-4" />
          Add Service
        </Button>
      </div>

      {airportServices.length === 0 ? (
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center ${isReadOnly ? "" : "cursor-pointer transition-colors hover:bg-muted/30"}`}
          onClick={isReadOnly ? undefined : addService}
          onKeyDown={isReadOnly ? undefined : (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              addService();
            }
          }}
          role={isReadOnly ? undefined : "button"}
          tabIndex={isReadOnly ? -1 : 0}
          aria-label={isReadOnly ? undefined : "Add service"}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-full bg-muted p-3">
              <Plus className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">No airport services added yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "Add Service" to get started
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {airportServices.map((service, index) => (
            <div key={index} className="grid gap-5 p-6 border rounded-lg bg-card relative shadow-sm hover:shadow-md transition-shadow">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-3 right-3 h-8 w-8 p-0"
                onClick={() => removeService(index)}
                disabled={isReadOnly}
              >
                <X className="h-4 w-4" />
              </Button>

              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Airport Service</Label>
                  <SearchableSelect
                    items={services.map((s) => ({
                      id: s.id,
                      code: s.service_code,
                      title: s.service_name,
                      description: s.airport || undefined,
                    }))}
                    value={service.airport_service_id}
                    onValueChange={(value) => updateService(index, "airport_service_id", parseInt(value.toString()))}
                    placeholder="Select service"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Service Date</Label>
                  <DateInput
                    value={service.service_date}
                    onChange={(value) => updateService(index, "service_date", value)}
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="border-t pt-5">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={service.free_of_charge}
                    onCheckedChange={(checked) => updateService(index, "free_of_charge", checked)}
                    disabled={isReadOnly}
                  />
                  <Label className="text-sm font-normal cursor-pointer">Free of Charge (FOC)</Label>
                </div>
              </div>

              <div className="border-t pt-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Notes</Label>
                  <Textarea
                    value={service.notes || ""}
                    onChange={(e) => updateService(index, "notes", e.target.value)}
                    placeholder="Add any additional notes..."
                    className="min-h-[100px]"
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="flex items-end justify-between border-t pt-3">
                <div>
                  {pricingTypeLabel(service.airport_services?.pricing_type) && (
                    <span className="inline-flex items-center rounded-full border border-border/70 bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      {pricingTypeLabel(service.airport_services?.pricing_type)}
                      {service.airport_services?.pricing_type?.toLowerCase() === "per_person" && (
                        <span className="ml-1">· {paxCount} pax</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Item total</p>
                  <PriceWithAdjustment
                    baseAmount={service.free_of_charge ? 0 : computeItemBaseAmount(service, service.airport_services, paxData)}
                    proposalData={paxData}
                    emptyLabel={service.free_of_charge ? "Free" : !service.airport_service_id ? "—" : undefined}
                    size="md"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AirportServicesStep;
